import {
  deserializeRPCMessage,
  IRPC,
  serializeRPCMessage,
} from "@prisma/studio-common";
import cors from "cors";
import express, { Response } from "express";
import http from "http";
import untildify from "untildify";
import { gzipSync } from "zlib";
import { PrismaChannel } from "./channels/prisma";
import { ProjectChannel } from "./channels/project";
import { TelemetryChannel } from "./channels/telemetry";
import debug from "./utils/debug";
import type { DriverAdapter } from "@prisma/driver-adapter-utils";

export interface IServerOptions {
  /** Port on which this server listens for requests */
  port: number;
  /** Absolute path to the prisma schema that will be displayed */
  schemaPath: string;
  /** Source code of the schema */
  schemaText: string;
  /** Path to Studio's frontend assets */
  staticAssetDir?: string;
  /** Hostname to bind the Express server to */
  hostname?: string;
  /** Optional driver adapter to use to connect to the database */
  adapter?: DriverAdapter;
  /** Prisma Client-specific options */
  prismaClient?: {
    /** Custom import resolves */
    resolve?: {
      "@prisma/engines"?: string;
      "@prisma/client"?: string;
      ".prisma/client"?: string;
    };
    forcePrismaLibrary?: boolean;
    forcePrismaBinary?: boolean;
    /** optional directUrl */
    directUrl?: string;
  };
  /** Controls if telemetry information is recorded */
  telemetry?: boolean;
  /** Versions of prisma dependencies reported (Only used for reporting, does not affect actually used prisma dependencies) */
  versions: {
    prisma: string;
    queryEngine?: string;
  };
  /** Controls if the server is in development mode */
  development?: boolean;
}

export class StudioServer {
  private server?: http.Server;
  private options: IServerOptions;
  private channels: {
    project: ProjectChannel;
    prisma: PrismaChannel;
    telemetry: TelemetryChannel;
  };

  constructor(options: IServerOptions) {
    this.options = options;
    this.options.schemaPath = untildify(this.options.schemaPath);

    this.channels = {
      project: new ProjectChannel(options),
      prisma: new PrismaChannel(options),
      telemetry: new TelemetryChannel(options),
    };
  }

  /**
   * Starts a server that serves the react app
   */
  public start = async () => {
    try {
      debug("Starting Studio server");

      const app = express();

      app.use(express.text());

      if (this.options.development) {
        /**
         * We'll use the Vite server for assets during development, so:
         * 1. Set up CORS to allow the Vite server to talk to us
         * 2. Do not set up a static asset directory, since assets with come from the Vite server
         */
        app.use(cors({ origin: "*" }));
      } else {
        /**
         * If not in development,
         * 1. Do not set up CORS because we don't need it.
         * 2. Set up a static asset directory, to server Studio's frontend assets
         */

        // Redirect, to allow for the root path to load up the databrowser page directly
        app.use(function (req, _res, next) {
          if (
            req.url === "/" ||
            req.url === "/databrowser.html" // Useful during tests, so that the test doesn't have to care if it is running with or without Vite
          ) {
            req.url = `/pages/http/databrowser.html`;
          }
          next();
        });

        const staticAssetDir = this.options.staticAssetDir;
        staticAssetDir &&
          app.use(
            express.static(staticAssetDir, {
              etag: false,
              setHeaders: res => {
                res.set("Cache-Control", "no-cache");
                res.set("Last-Modified", new Date().toUTCString());
              },
            })
          );
      }

      app.post("/api", async (req, res: Response<any>) => {
        debug("Incoming request: ", req.body);
        const requestBody = deserializeRPCMessage(req.body) as IRPC["request"];
        const { requestId, channel, action, payload } = requestBody;

        let response: IRPC["response"];

        switch (channel) {
          case "project":
            response = await this.channels.project.respond(requestBody as any);
            break;
          case "prisma":
            response = await this.channels.prisma.respond(requestBody as any);
            break;
          case "telemetry":
            response = await this.channels.telemetry.respond(
              requestBody as any
            );
            break;
          default:
            debug("Unimplemented `channel`, ignoring request:", requestBody);
            response = {
              requestId,
              channel: `-${channel}` as any,
              action: action as any,
              payload: {
                error: null,
                data: null,
              },
            };
            break;
        }

        res.setHeader("Content-Type", "application/json");
        res.setHeader("Content-Encoding", "gzip");
        res.send(
          gzipSync(
            new Uint8Array(Buffer.from(serializeRPCMessage(response), "utf8"))
          )
        ); // GZip response since DMMF can be quite large
        debug("Outgoing response: ", response);
      });

      this.server = http.createServer(app);

      this.server.listen(this.options.port, this.options.hostname, () => {
        debug(
          `Studio server is up at http://${
            this.options.hostname || "0.0.0.0" // Express listens on `0.0.0.0` by default
          }:${this.options.port}/`
        );
      });
    } catch (e) {
      console.log("An error occured while starting Studio:\n\n", e);
      process.exit(1);
    }
  };

  /**
   * Stops the server
   *
   * @param reason Optional parameter to log why the server was stopped
   */
  public stop = (reason?: string) => {
    debug("Stopping Studio server. Reason:", reason);
    if (!this.server) {
      return;
    }

    this.server.close(err => {
      if (err) {
        debug("Unable to close server: ", err);
      } else {
        debug("Closed out remaining connections");
      }
    });
  };
}
